import { Component, OnInit } from '@angular/core'
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms'
import { Actions, ofType } from '@ngrx/effects'
import { Store } from '@ngrx/store'
import { of } from 'rxjs'
import { catchError, take, timeout } from 'rxjs/operators'
import { AppState } from 'src/app/core/core.state'
import { NotificationService } from 'src/app/core/notification.service'
import { AccessRights } from 'src/app/features/role/user-role/user-role'
import { ConfigOptionsKeys } from 'src/app/shared/constants'
import {
  ScheduleFilterSettings,
  ScheduleItemsInScheduleSettings,
  ScheduleSettingsWebOption
} from '../configuration-option'
import { configurationOptionActions } from '../configuration-option.actions'
import { configurationOptionSelectors } from '../configuration-option.selectors'
import { ConfigurationOptionService } from '../configuration-option.service'

@Component({
  selector: 'configuration-option-schedule-settings-tab',
  templateUrl: './show.component.html'
})
export class ConfigurationOptionScheduleSettingsTabComponent
  implements OnInit
{
  public form: UntypedFormGroup
  public isCollapsed = false
  public accessRights = AccessRights
  public scheduleSettings: ScheduleSettingsWebOption
  public readonly scheduleOptions: Array<{
    key: keyof ScheduleItemsInScheduleSettings
    name: string
    yesId: string
    noId: string
    label: string
    tooltip?: string
  }> = [
    {
      key: 'showCourses',
      name: 'itemsInScheduleShowCourses',
      yesId: 'itemsInScheduleShowCoursesYes',
      noId: 'itemsInScheduleShowCoursesNo',
      label: 'configurationOptionModule.showCourses',
      tooltip: 'configurationOptionModule.showCoursesTooltip'
    },
    {
      key: 'showFullBookings',
      name: 'itemsInScheduleShowFullBookings',
      yesId: 'itemsInScheduleShowFullBookingsYes',
      noId: 'itemsInScheduleShowFullBookingsNo',
      label: 'configurationOptionModule.showFullBookings',
      tooltip: 'configurationOptionModule.showFullBookingsTooltip'
    },
    {
      key: 'showSlotGeneratedBookings',
      name: 'itemsInScheduleShowSlotGeneratedBookings',
      yesId: 'itemsInScheduleShowSlotGeneratedBookingsYes',
      noId: 'itemsInScheduleShowSlotGeneratedBookingsNo',
      label: 'configurationOptionModule.showSlotGeneratedBookings',
      tooltip: 'configurationOptionModule.showSlotGeneratedBookingsTooltip'
    },
    {
      key: 'showPotentialActivities',
      name: 'itemsInScheduleShowPotentialActivities',
      yesId: 'itemsInScheduleShowPotentialActivitiesYes',
      noId: 'itemsInScheduleShowPotentialActivitiesNo',
      label: 'configurationOptionModule.showPotentialActivities',
      tooltip: 'configurationOptionModule.showPotentialActivitiesTooltip'
    }
  ]
  public readonly scheduleFilterOptions: Array<{
    key: keyof ScheduleFilterSettings
    name: string
    yesId: string
    noId: string
    label: string
  }> = [
    {
      key: 'showTimeFilter',
      name: 'scheduleFilterShowTimeFilter',
      yesId: 'scheduleFilterShowTimeFilterYes',
      noId: 'scheduleFilterShowTimeFilterNo',
      label: 'configurationOptionModule.showTimeFilter'
    },
    {
      key: 'showLocationFilter',
      name: 'scheduleFilterShowLocationFilter',
      yesId: 'scheduleFilterShowLocationFilterYes',
      noId: 'scheduleFilterShowLocationFilterNo',
      label: 'configurationOptionModule.showLocationFilter'
    },
    {
      key: 'showMembershipFilter',
      name: 'scheduleFilterShowMembershipFilter',
      yesId: 'scheduleFilterShowMembershipFilterYes',
      noId: 'scheduleFilterShowMembershipFilterNo',
      label: 'configurationOptionModule.showMembershipFilter'
    },
    {
      key: 'showSupervisorFilter',
      name: 'scheduleFilterShowSupervisorFilter',
      yesId: 'scheduleFilterShowSupervisorFilterYes',
      noId: 'scheduleFilterShowSupervisorFilterNo',
      label: 'configurationOptionModule.showSupervisorFilter'
    }
  ]

  constructor(
    private store: Store<AppState>,
    private fb: UntypedFormBuilder,
    private configOptionService: ConfigurationOptionService,
    private notificationService: NotificationService,
    private actions$: Actions
  ) {}

  ngOnInit(): void {
    this.form = this.fb.group({
      itemsInSchedule: this.fb.group({
        showCourses: [false, []],
        showFullBookings: [false, []],
        showSlotGeneratedBookings: [false, []],
        showPotentialActivities: [false, []]
      }),
      scheduleFilter: this.fb.group({
        showTimeFilter: [false, []],
        showLocationFilter: [false, []],
        showMembershipFilter: [false, []],
        showSupervisorFilter: [false, []]
      })
    })

    this.store
      .select(
        configurationOptionSelectors.selectByKeyWithType<ScheduleSettingsWebOption>(
          ConfigOptionsKeys.SCHEDULE_SETTINGS
        )
      )
      .pipe(take(1))
      .subscribe((config) => {
        this.scheduleSettings = this.buildFormPatchValue(config)
      })
  }

  public onFormSubmit(): void {
    if (this.form.invalid) {
      return
    }

    this.actions$
      .pipe(
        ofType(configurationOptionActions.upsertMany),
        take(1),
        timeout(1000),
        catchError(() => of(null))
      )
      .subscribe((action) =>
        action
          ? this.notificationService.emit('alerts.success', 'success')
          : null
      )

    this.configOptionService.updateOrCreateMany({
      [ConfigOptionsKeys.SCHEDULE_SETTINGS]: this.form.value
    })
  }

  public isItemInScheduleEnabled(
    field: keyof ScheduleItemsInScheduleSettings
  ): boolean {
    return Boolean(this.form.get(['itemsInSchedule', field])?.value)
  }

  public isScheduleFilterEnabled(field: keyof ScheduleFilterSettings): boolean {
    return Boolean(this.form.get(['scheduleFilter', field])?.value)
  }

  public patchItemInSchedule(
    field: keyof ScheduleItemsInScheduleSettings,
    value: boolean
  ): void {
    this.form.get(['itemsInSchedule', field])?.patchValue(value)
  }

  public patchScheduleFilter(
    field: keyof ScheduleFilterSettings,
    value: boolean
  ): void {
    this.form.get(['scheduleFilter', field])?.patchValue(value)
  }

  private buildFormPatchValue(
    value?: ScheduleSettingsWebOption
  ): ScheduleSettingsWebOption {
    return {
      itemsInSchedule: {
        showCourses: value?.itemsInSchedule?.showCourses ?? false,
        showFullBookings: value?.itemsInSchedule?.showFullBookings ?? false,
        showSlotGeneratedBookings:
          value?.itemsInSchedule?.showSlotGeneratedBookings ?? false,
        showPotentialActivities:
          value?.itemsInSchedule?.showPotentialActivities ?? false
      },
      scheduleFilter: {
        showTimeFilter: value?.scheduleFilter?.showTimeFilter ?? false,
        showLocationFilter: value?.scheduleFilter?.showLocationFilter ?? false,
        showMembershipFilter:
          value?.scheduleFilter?.showMembershipFilter ?? false,
        showSupervisorFilter:
          value?.scheduleFilter?.showSupervisorFilter ?? false
      }
    }
  }
}
